import { DrawerOpenEvent } from '@theme/theme-drawer';

const catalogCache = new Map();

class CartDrawerLook extends HTMLElement {
  #abortController = new AbortController();
  #products = [];

  connectedCallback() {
    const drawer = this.closest('theme-drawer');
    drawer?.addEventListener(DrawerOpenEvent.eventName, this.#renderRandomProducts, {
      signal: this.#abortController.signal,
    });
    this.#loadProducts();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  async #loadProducts() {
    const collectionHandle = this.dataset.collectionHandle;
    const cacheKey = collectionHandle || 'all';
    let request = catalogCache.get(cacheKey);

    if (!request) {
      const root = window.Shopify?.routes?.root || '/';
      const endpoint = collectionHandle
        ? `${root}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`
        : `${root}products.json?limit=250`;

      request = fetch(endpoint, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load cart recommendations: ${response.status}`);
          return response.json();
        })
        .then((payload) => payload.products || []);
      catalogCache.set(cacheKey, request);
    }

    try {
      this.#products = await request;
      this.#renderRandomProducts();
    } catch (error) {
      console.warn('[cart-drawer-look]', error);
      const container = this.querySelector('[data-look-products]');
      if (container) container.textContent = '';
    }
  }

  #renderRandomProducts = () => {
    if (!this.isConnected || this.#products.length === 0) return;

    const excludedIds = new Set((this.dataset.excludedProductIds || '').split(',').filter(Boolean));
    const count = Math.max(1, Number(this.dataset.count) || 2);
    const candidates = this.#products.filter((product) => !excludedIds.has(String(product.id)) && product.variants?.length);

    for (let index = candidates.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [candidates[index], candidates[randomIndex]] = [candidates[randomIndex], candidates[index]];
    }

    const container = this.querySelector('[data-look-products]');
    if (!container) return;
    container.replaceChildren(...candidates.slice(0, count).map((product) => this.#createProductCard(product)));
  };

  #createProductCard(product) {
    const variant = product.variants.find((item) => item.available) || product.variants[0];
    const root = window.Shopify?.routes?.root || '/';
    const productUrl = product.handle ? `${root}products/${product.handle}` : '#';
    const card = document.createElement('article');
    card.className = 'jm-cart-look__product';

    const imageLink = document.createElement('a');
    imageLink.className = 'jm-cart-look__image';
    imageLink.href = productUrl;
    imageLink.setAttribute('aria-label', product.title);

    const imageUrl = product.images?.[0]?.src || product.image?.src;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.images?.[0]?.alt || product.title;
      image.loading = 'lazy';
      image.width = 80;
      image.height = 96;
      imageLink.append(image);
    }

    const details = document.createElement('div');
    details.className = 'jm-cart-look__details';
    const title = document.createElement('a');
    title.className = 'jm-cart-look__title';
    title.href = imageLink.href;
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'jm-cart-look__price';
    price.textContent = this.#formatMoney(Math.round(Number(variant.price) * 100));
    details.append(title, price);

    const productForm = document.createElement('product-form-component');
    productForm.className = 'jm-cart-look__form';
    productForm.dataset.sectionId = 'cart-drawer-section';
    productForm.dataset.productId = String(product.id);
    productForm.dataset.productUrl = imageLink.href;
    productForm.dataset.quantityDefault = '1';
    productForm.setAttribute('on:submit', '/handleSubmit');

    const liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'assertive');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('ref', 'liveRegion');

    const form = document.createElement('form');
    form.action = window.Theme?.routes?.cart_add_url || '/cart/add';
    form.method = 'post';
    form.dataset.type = 'add-to-cart-form';

    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.value = variant.id;
    variantInput.setAttribute('ref', 'variantId');

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';

    const addToCart = document.createElement('add-to-cart-component');
    addToCart.dataset.addToCartAnimation = 'false';
    const button = document.createElement('button');
    button.className = 'button jm-cart-look__button';
    button.type = 'submit';
    button.name = 'add';
    button.setAttribute('ref', 'addToCartButton');
    button.setAttribute('on:click', '/handleClick');
    button.disabled = !variant.available;
    button.textContent = variant.available ? 'AGREGAR' : 'AGOTADO';
    addToCart.append(button);
    form.append(variantInput, quantityInput, addToCart);
    productForm.append(liveRegion, form);

    card.append(imageLink, details, productForm);
    return card;
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'MXN';
    return new Intl.NumberFormat(document.documentElement.lang || 'es-MX', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }
}

if (!customElements.get('cart-drawer-look')) {
  customElements.define('cart-drawer-look', CartDrawerLook);
}
